import { useEffect } from 'react';
import i18n, { SUPPORTED_LANGUAGES, baseTag, isSupported } from './index';
import { apiJson } from '../lib/api';
import { updateMe } from '../lib/me';

type Props = { children: React.ReactNode };

/**
 * Handles one-time locale reconciliation on app boot:
 *   - If the user has a server-side preferred_language, switch to it.
 *   - Else, if the device language is supported, switch + persist to server.
 *   - Else, stay on English and leave the server value empty.
 *
 * Also keeps <html lang> / <html dir> in sync with the active language.
 */
export function LocaleProvider({ children }: Props) {
  useEffect(() => {
    const apply = (lang: string) => {
      const root = document.documentElement;
      root.lang = lang;
      root.dir = lang === 'he' ? 'rtl' : 'ltr';
    };

    apply(i18n.language || 'en');
    const handler = (lng: string) => apply(lng);
    i18n.on('languageChanged', handler);

    // Fetch the user's server-side preference. Unauthenticated users 401 here —
    // that's fine, we just stay on the detector's choice.
    (async () => {
      try {
        const me = await apiJson<{ preferred_language: string }>('/api/me/');
        const serverLang = (me.preferred_language || '').toLowerCase();

        if (serverLang && isSupported(serverLang)) {
          if (i18n.language !== serverLang) await i18n.changeLanguage(serverLang);
          return;
        }

        // No server preference yet — see if device language is one we support.
        const detected = baseTag(navigator.language || 'en');
        if (isSupported(detected)) {
          if (i18n.language !== detected) await i18n.changeLanguage(detected);
          try {
            await updateMe({ preferred_language: detected });
          } catch {
            // Persisting is best-effort; the UI still reflects the detected lang.
          }
        }
      } catch {
        // Not signed in or API down — keep detector's default.
      }
    })();

    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);

  return <>{children}</>;
}

export { SUPPORTED_LANGUAGES };
